import { useState, useMemo } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getEAProject, submitEATask } from '../services/ea-api'
import { getClient } from '../lib/aperture'
import EAVendorBadge from '../components/ea/EAVendorBadge'
import EAPhaseIndicator from '../components/ea/EAPhaseIndicator'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const PHASE_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  design: 'Design',
  standards_gate: 'Standards Gate',
  deployment: 'Deployment',
  qc: 'QC Validation',
  deliverables: 'Deliverables',
  complete: 'Complete',
}

const VENDOR_KEYWORDS: Record<string, string[]> = {
  Arista: ['arista', 'eos', 'cloudvision', 'cvp', 'veos', 'ceos'],
  PaloAlto: ['palo alto', 'paloalto', 'panorama', 'prisma', 'pan-os', 'panos', 'firewall'],
  Nutanix: ['nutanix', 'prism', 'ahv', 'acropolis', 'hci'],
  Azure: ['azure', 'microsoft', 'entra', 'active directory', 'office 365', 'o365'],
  Cisco: ['cisco', 'ios-xe', 'ios-xr', 'nexus', 'catalyst', 'meraki', 'aci', 'dnac', 'ise'],
}

const PHASE_KEYWORDS: Record<string, string[]> = {
  discovery: ['discover', 'requirements', 'gather', 'questionnaire', 'scope', 'assess', 'inventory', 'audit'],
  design: ['design', 'hld', 'lld', 'architecture', 'diagram', 'topology', 'bom', 'bill of materials'],
  standards_gate: ['standards', 'compliance', 'validate', 'check', 'gate', 'review', 'best practice'],
  deployment: ['deploy', 'config', 'implement', 'provision', 'terraform', 'ansible', 'migrate', 'install'],
  qc: ['qc', 'quality', 'test', 'verify', 'validation', 'finding', 'remediation'],
  deliverables: ['deliverable', 'promote', 'handover', 'package', 'export', 'final'],
}

function detectVendors(text: string): string[] {
  const lower = text.toLowerCase()
  const detected: string[] = []
  for (const [vendor, keywords] of Object.entries(VENDOR_KEYWORDS)) {
    if (keywords.some((kw) => lower.includes(kw))) {
      detected.push(vendor)
    }
  }
  return detected
}

function detectPhase(text: string): string | null {
  const lower = text.toLowerCase()
  let bestPhase: string | null = null
  let bestScore = 0
  for (const [phase, keywords] of Object.entries(PHASE_KEYWORDS)) {
    const score = keywords.filter((kw) => lower.includes(kw)).length
    if (score > bestScore) {
      bestScore = score
      bestPhase = phase
    }
  }
  return bestPhase
}

export default function EATaskSubmit() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [taskDescription, setTaskDescription] = useState('')

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['ea-project', projectId],
    queryFn: () => getEAProject(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const submitMutation = useMutation({
    mutationFn: (data: { task_description: string }) => submitEATask(projectId!, data),
    onSuccess: () => {
      getClient()?.capture('output_metric', { metric_key: 'ea_tasks_submitted', value: 1 })
      navigate(`/ea/projects/${projectId}/pipeline`)
    },
  })

  const detectedVendors = useMemo(() => detectVendors(taskDescription), [taskDescription])
  const detectedPhase = useMemo(() => detectPhase(taskDescription), [taskDescription])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!taskDescription.trim()) return
    submitMutation.mutate({ task_description: taskDescription.trim() })
  }

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)',
            }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading project...</p>
        </div>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="text-center py-20">
        <div
          className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
          style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.2)' }}
        >
          <i className="fa-solid fa-exclamation-triangle text-3xl" style={{ color: '#ef4444' }}></i>
        </div>
        <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>Project not found</h3>
        <Link
          to="/ea/projects"
          className="px-6 py-3 rounded-xl text-sm font-medium inline-flex items-center space-x-2 transition-all duration-200 hover:scale-105"
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
            border: '1px solid rgba(96, 165, 250, 0.4)',
            color: '#e2e8f0',
          }}
        >
          <i className="fa-solid fa-arrow-left"></i>
          <span>Back to Projects</span>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to={`/ea/projects/${projectId}`}
          className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-105"
          style={{
            background: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid rgba(100, 180, 255, 0.15)',
            color: 'rgba(148, 163, 184, 0.8)',
          }}
        >
          <i className="fa-solid fa-arrow-left"></i>
        </Link>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Submit Task</h1>
          <p className="text-sm mt-0.5" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            Describe what you need the EA agents to do
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main form - 2 cols */}
        <div className="lg:col-span-2 space-y-6">
          <form onSubmit={handleSubmit}>
            {/* Task description textarea */}
            <div className="rounded-xl p-6" style={glassPanel}>
              <label className="block mb-3">
                <span className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>
                  Task Description
                </span>
                <span className="text-xs ml-2" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                  Use natural language to describe the task
                </span>
              </label>
              <textarea
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                placeholder="e.g., Generate the Arista network design HLD and LLD for the campus switching layer, including VLAN assignments, MLAG configuration, and BGP underlay..."
                rows={8}
                className="w-full rounded-xl p-4 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                style={{
                  background: 'rgba(30, 41, 59, 0.5)',
                  border: '1px solid rgba(100, 180, 255, 0.15)',
                  color: '#e2e8f0',
                }}
                aria-label="Task description"
              />

              {/* Auto-detection previews */}
              {taskDescription.trim().length > 0 && (
                <div className="mt-4 space-y-3">
                  {/* Detected vendors */}
                  {detectedVendors.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                        <i className="fa-solid fa-wand-magic-sparkles mr-1"></i>
                        Detected Vendors:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {detectedVendors.map((v) => (
                          <EAVendorBadge key={v} vendor={v} size="sm" />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Detected phase */}
                  {detectedPhase && (
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                        <i className="fa-solid fa-route mr-1"></i>
                        Likely Phase:
                      </span>
                      <span
                        className="px-2.5 py-0.5 text-xs font-medium rounded-full"
                        style={{
                          background: 'rgba(139, 92, 246, 0.15)',
                          color: '#a78bfa',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                        }}
                      >
                        {PHASE_LABELS[detectedPhase] || detectedPhase}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Submit error */}
              {submitMutation.isError && (
                <div
                  className="mt-4 rounded-lg p-3 flex items-center space-x-2"
                  style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                  }}
                >
                  <i className="fa-solid fa-circle-exclamation" style={{ color: '#f87171' }}></i>
                  <span className="text-sm" style={{ color: '#f87171' }}>
                    Failed to submit task. Please try again.
                  </span>
                </div>
              )}

              {/* Submit button */}
              <div className="mt-6 flex items-center justify-between">
                <Link
                  to={`/ea/projects/${projectId}/pipeline`}
                  className="text-sm font-medium transition-colors"
                  style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                >
                  View Pipeline
                </Link>
                <button
                  type="submit"
                  disabled={!taskDescription.trim() || submitMutation.isPending}
                  className="px-6 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(139, 92, 246, 0.4) 100%)',
                    border: '1px solid rgba(96, 165, 250, 0.5)',
                    color: '#e2e8f0',
                  }}
                >
                  {submitMutation.isPending ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin"></i>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane"></i>
                      <span>Submit Task</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Project context sidebar - 1 col */}
        <div className="space-y-6">
          <div className="rounded-xl p-5" style={glassPanel}>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
              Project Context
            </h3>

            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Project</p>
                <p className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>{project.project_name}</p>
                <p className="text-xs font-mono" style={{ color: 'rgba(148, 163, 184, 0.4)' }}>{project.project_code}</p>
              </div>

              <div>
                <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Client</p>
                <p className="text-sm" style={{ color: '#e2e8f0' }}>{project.client_name}</p>
              </div>

              <div>
                <p className="text-xs font-medium mb-1.5" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Vendors in Scope</p>
                {project.vendors.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {project.vendors.map((v) => (
                      <EAVendorBadge key={v} vendor={v} size="md" />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.4)' }}>No vendors assigned</p>
                )}
              </div>

              <div>
                <p className="text-xs font-medium mb-1.5" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Current Phase</p>
                <EAPhaseIndicator currentPhase={project.current_phase} compact />
              </div>

              {project.engagement_type && (
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Engagement</p>
                  <p className="text-sm capitalize" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                    {project.engagement_type.replace('_', ' ')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quick tips */}
          <div className="rounded-xl p-5" style={glassPanel}>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'rgba(100, 116, 139, 0.6)' }}>
              Tips
            </h3>
            <ul className="space-y-2 text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
              <li className="flex items-start space-x-2">
                <i className="fa-solid fa-lightbulb mt-0.5" style={{ color: '#f59e0b' }}></i>
                <span>Mention vendor names to auto-route to the right pipeline</span>
              </li>
              <li className="flex items-start space-x-2">
                <i className="fa-solid fa-lightbulb mt-0.5" style={{ color: '#f59e0b' }}></i>
                <span>Use phase keywords like "design", "deploy", or "QC" for phase detection</span>
              </li>
              <li className="flex items-start space-x-2">
                <i className="fa-solid fa-lightbulb mt-0.5" style={{ color: '#f59e0b' }}></i>
                <span>Be specific about deliverables you expect (HLD, LLD, BOM, configs)</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}
